import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Search } from "lucide-react";

const FAQS = [
  {
    q: "What are your opening hours?",
    a: "We're open 7 days a week. Monday to Friday: 11h00 – 18h00. Saturdays, Sundays, public and school holidays: 09h00 – 18h00.",
  },
  {
    q: "How long does it take to get to Cool Runnings from the Cape Town City Centre?",
    a: "Just under 25 minutes — we're only 25km from the city centre, straight up the N1 (Exit 23).",
  },
  {
    q: "When is the last ride of the day?",
    a: "Our last ride is at 17h45 — arrive a little earlier so you have time to check in and take the lift up.",
  },
  {
    q: "When are you closed?",
    a: "Two reasons only: if it rains (even a drizzle pauses the track for safety) and on 25 December, Christmas Day. Every other public holiday, we ride.",
  },
  {
    q: "How much do rides cost?",
    a: "Pay per person, per ride. One ride is R110 for children (3–14) and R120 for adults (15+). Save 10% when you buy online!",
  },
  {
    q: "How do I order tickets online?",
    a: "Visit our online shop (the same place our 'Buy Tickets' buttons take you) and select your tickets. Then present your order and payment confirmation at the ticket office — or simply buy rides on site on the day of your arrival. Please note: platters and party packs can't be ordered online — they're booked with your party or bought at the kiosk.",
  },
  {
    q: "Can I order food for a group?",
    a: "Yes — with a booked party. Party packs start at R60, kids feasts at R125 and platters run R600–R850 (one platter feeds 8–10; halaal and kosher options on request). Party packs and combos need 2 days' notice; platters need 4 days' notice with full payment upfront — and because you pay for the platters anyway, your platter payment automatically reserves your tables, so no deposit is needed. Email janet@coolrunnings.capetown with your date, time and an alternative date — we'll confirm if there's availability.",
  },
  {
    q: "Do I need a booking or reservation to ride?",
    a: "No — just come along any day and time that suits you. Bigger groups (20+) should contact us before arrival so we can prepare.",
  },
  {
    q: "Do my tickets expire?",
    a: "Tickets and rides are non-refundable, but they never expire — unused rides stay valid until used. They can even be shared between different people.",
  },
  {
    q: "Is there an age or height limit?",
    a: "Any rider needs to be at least 3 years old, and riders must be 1,35 m tall to ride alone. Children aged 3–7 ride with an adult aged 15 or older, and children under 3 can't ride.",
  },
  {
    q: "Can two people ride on one sledge?",
    a: "Yes — but only an adult with one child. Two adults can't share, because the maximum weight of 150 kg per sledge may not be exceeded.",
  },
  {
    q: "Is there a weight limit?",
    a: "Yes — 150 kg per sledge.",
  },
  {
    q: "Do I pay per person or per sledge?",
    a: "You pay per person, per ride, regardless of age.",
  },
  {
    q: "How long does a ride last?",
    a: "About 3½ minutes on the lift to the top of the hill and about 2 minutes on the downhill track — bearing in mind that you control the speed yourselves.",
  },
  {
    q: "Can rides on a ticket package be shared?",
    a: "Yes — the rides on our ticket options can be shared between different people. Just remember: child rides can't be used for adults, but adult rides can be used for children!",
  },
  {
    q: "What's the difference between a Working Day Pass and a Day Pass?",
    a: "A Working Day Pass is valid only on working days. It cannot be used on weekends, public holidays or school holidays — for that you'd want the regular Day Pass.",
  },
  {
    q: "Is a Day Pass transferable?",
    a: "No — each person needs their own Day Pass, regardless of age.",
  },
  {
    q: "Are you selling alcohol?",
    a: "Yes — we have a licensed bar offering a variety of ciders, beers and wines for our adult patrons.",
  },
  {
    q: "Can I cater for my own party?",
    a: "Unfortunately, no outside food, party packs or beverages may come onto our site — the one exception is a birthday cake for parties. You can bring a cake if you have a booking with a paid deposit; without a booking we charge a R250 cakeage fee per cake. We don't charge entrance or parking — we just can't be used as a picnic spot. Book us to cater your event and a table is reserved for you and your guests.",
  },
  {
    q: "Can I buy halaal or kosher food?",
    a: "Yes — although we're not certified ourselves, we offer certain options and where necessary subcontract your order to our certified supplier. Please ask our staff.",
  },
  {
    q: "Do I have to wear a helmet?",
    a: "No — but you're welcome to bring your own and use it.",
  },
  {
    q: "Do I have to wear a safety belt?",
    a: "No.",
  },
  {
    q: "Do I have to wear closed shoes?",
    a: "It's advisable, but not a requirement.",
  },
  {
    q: "Do I have to pay for parking?",
    a: "No — parking is free of charge.",
  },
  {
    q: "Do I have to pay an entrance fee?",
    a: "No — entrance is free of charge.",
  },
  {
    q: "Are pregnant women allowed to ride?",
    a: "No, unfortunately not — the safety of mom and baby comes first.",
  },
  {
    q: "Are disabled people allowed to ride?",
    a: "Possibly — speak to the Operations Manager to verify whether riding is safe with the kind of disability in question.",
  },
  {
    q: "Is the park wheelchair friendly?",
    a: "Not fully, but we try to assist and help wherever possible.",
  },
  {
    q: "Can I bring my pet to Cool Runnings?",
    a: "We love animals, but unfortunately they're not allowed on our site — with the exception of service dogs.",
  },
  {
    q: "Do you offer a free birthday ride?",
    a: "Yes! Visit on any day during your birthday month — bring your original South African ID or birth certificate and your first ride is free!",
  },
  {
    q: "What happens when there's loadshedding?",
    a: "Nothing. We're powered by solar — the park is 100% loadshedding-free, and there's free Wi-Fi on site.",
  },
  {
    q: "Are we responsible for a rider's driving skills and behaviour while riding?",
    a: "No — we're providing a sports installation for outdoor entertainment, and riders are personally responsible for riding responsibly.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  // searching looks through ALL answers; otherwise the first 10 show and the rest sit behind "Read more"
  const filtered = q ? FAQS.filter((f) => (f.q + " " + f.a).toLowerCase().includes(q)) : FAQS;
  const visible = q ? filtered : showAll ? FAQS : FAQS.slice(0, 10);

  return (
    <section id="faq" className="mx-auto max-w-4xl px-4 py-24 sm:px-6" data-testid="faq-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7 }}
        className="text-center"
      >
        <h2 className="text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>FREQUENTLY ASKED QUESTIONS</h2>
      </motion.div>

      <div className="mt-10" data-testid="faq-search-wrap">
        <label className="relative block">
          <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#8a7b5e]" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the questions — try 'birthday', 'rain', 'last ride'…"
            aria-label="Search the frequently asked questions"
            className="input-dark w-full !py-4 !pl-11 text-base"
            data-testid="faq-search-input"
          />
        </label>
        {q && (
          <p className="mt-2 text-xs font-bold text-[#8a7b5e]" data-testid="faq-search-count">
            {filtered.length} {filtered.length === 1 ? "answer" : "answers"} found for “{query.trim()}”
          </p>
        )}
      </div>

      <div className="mt-8 space-y-3">
        {visible.map((f, i) => (
          <motion.div
            key={f.q}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: Math.min(i * 0.03, 0.4) }}
            className={`card-dark overflow-hidden transition-colors ${open === f.q ? "border-[#f8b818]/40" : ""}`}
            data-testid={`faq-item-${i}`}
          >
            <button
              onClick={() => setOpen(open === f.q ? "" : f.q)}
              className="flex w-full items-center justify-between gap-4 p-6 text-left"
              data-testid={`faq-toggle-${i}`}
              aria-expanded={open === f.q}
            >
              <span className="font-extrabold">{f.q}</span>
              <ChevronDown
                size={18}
                className={`shrink-0 text-[#f8b818] transition-transform duration-300 ${open === f.q ? "rotate-180" : ""}`}
              />
            </button>
            {open === f.q && (
              <motion.p
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden px-6 pb-6 text-base leading-relaxed text-[#3d362c]"
                data-testid={`faq-answer-${i}`}
              >
                {f.a}
              </motion.p>
            )}
          </motion.div>
        ))}
        {q && filtered.length === 0 && (
          <p className="card-dark p-6 text-center text-sm font-bold text-[#8a7b5e]" data-testid="faq-search-empty">
            No answer for “{query.trim()}” yet — try another word, or leave a message for the front desk and we'll
            reply within a working day.
          </p>
        )}
      </div>

      {!q && FAQS.length > 10 && (
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              if (showAll) setOpen("");
              setShowAll(!showAll);
            }}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#241c0d]/15 bg-[#faf5ea] px-6 py-3 text-xs font-extrabold uppercase tracking-widest text-[#241c0d] transition-all hover:border-[#f8b818] hover:text-[#c87700]"
            aria-expanded={showAll}
            data-testid="faq-read-more-btn"
          >
            {showAll ? "Read less" : `Read more — ${FAQS.length - 10} more questions`}
            <ChevronDown size={14} className={`transition-transform duration-300 ${showAll ? "rotate-180" : ""}`} />
          </button>
        </div>
      )}
    </section>
  );
}
